import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL } from '../../../config/api';
import AiMark from './AiMark';
import { VIDEO_SECTIONS } from '../AvLibrary/data/videos';
import VideoPlayerModal from './VideoPlayerModal';
import { PODCASTS } from '../AvLibrary/data/podcasts';
import { ALERT_SOURCES } from '../FraudAlerts/alertSources';
import { isNew } from '../FraudAlerts/alertHelpers';

// The framed article header shared by every CyberFraud Alert page - the four
// agency alerts and ScamWire (10-28): a black frame with the page title on
// its top border, the sub-tab row (CyberFraud Alert / Av Library / Dark
// Psychology / Detection) and, when no panel is open, whatever the page puts
// in the hero (the ScamWire photo, or an agency's seal-and-name banner).
//
// CyberFraud Alert lists every category - IC3, SSA, IRS, FTC and ScamWire -
// under tabs, like the Av Library panel; it loads its lists the first time it
// opens. Av Library has one tab per agency plus Podcasts; a video picked there
// plays in an overlay on this page (10-28), so closing it leaves the reader
// where they were. Dark Psychology and Detection cross-link the feature
// pages. Rows are whole-row links.
// The CyberFraud Alert list carries the same read markers as the hub table
// (10-28): a Reviewed column with √ once the reader has opened the alert, and
// the blue tint on alerts published in the last 30 days.
// `current` ({ kind: 'alert', id } or { kind: 'hot-scam', slug }) is left out
// of the ScamWire list so a page never links to itself.

const DARK_PSYCHOLOGY_ITEMS = [
  { label: 'Pig Butchering / Crypto', to: '/dashboard/pig-butchering-crypto' },
  { label: 'Catfishing / Romance', to: '/dashboard/catfishing-romance' },
  { label: 'The Day After', to: '/dashboard/the-day-after' },
];

const DETECTION_ITEMS = [
  { label: 'Deepfake Detection', to: '/dashboard/deepfake' },
  { label: 'Image Search', to: '/dashboard/image-check' },
];

const PODCAST_ITEMS = PODCASTS.map((p) => ({
  label: p.title,
  to: `/dashboard/av-library/podcast?podcast=${p.slug}`,
}));

// Short tab names; the panel heading carries the agency's full name.
const AGENCY_TAB_LABEL = { 'fbi-ic3': 'IC3', ic3: 'IC3', ssa: 'SSA', irs: 'IRS', ftc: 'FTC' };

const AV_SUB_TABS = [
  ...VIDEO_SECTIONS.map((s) => ({
    id: s.slug,
    label: AGENCY_TAB_LABEL[s.slug] || s.label,
    heading: `${s.label} Video`,
    action: 'Watch',
    items: s.videos.map((v) => ({ label: v.title, video: { ...v, section: s.slug } })),
  })),
  { id: 'podcasts', label: 'Podcasts', heading: 'Podcast', action: 'Listen', items: PODCAST_ITEMS },
];

export const SUB_TABS = [
  { id: 'scamwire', label: 'CyberFraud Alert', heading: 'CyberFraud Alert', action: 'Read' },
  { id: 'av-library', label: 'Av Library', heading: 'Video / Podcast', action: 'Watch' },
  { id: 'dark-psychology', label: 'Dark Psychology', heading: 'Scammer Behavioral Screening', action: 'Go to' },
  { id: 'detection', label: 'Detection', heading: 'Unmask', action: 'Go to' },
];

// Fixed height (about three rows) rather than max-height, so every dropdown is
// the same size and the framed box never jumps between tabs. Longer lists
// scroll inside it.
const PANEL_SCROLL = 'h-[168px] overflow-y-auto';

const ROW_BASE = 'group flex w-full items-center justify-between border-b border-gray-300 py-3 pr-6 text-left';
const ROW_CLASS = `${ROW_BASE} hover:bg-gray-50`;
// New (last 30 days) alerts carry the hub table's tint, and its hover shade.
const NEW_ROW_CLASS = `${ROW_BASE} bg-[#E4F0F8] hover:bg-[#D6E7F4]`;

// Reviewed marker, same as the hub table: √ once opened, blank until then.
const ReviewedMark = ({ viewed }) => (
  <span
    className="w-20 flex-shrink-0 text-center text-lg text-black"
    title={viewed ? 'You have opened this report' : 'Not opened yet'}
    aria-label={viewed ? 'Reviewed' : 'Not reviewed'}
  >
    {viewed ? '√' : ''}
  </span>
);

const RowBody = ({ item, action, showViewed = false }) => (
  <>
    {showViewed && <ReviewedMark viewed={item.viewed} />}
    <span className="flex-1 text-lg text-black group-hover:text-blue-600">{item.label}</span>
    <span className="text-lg text-black group-hover:text-blue-600 group-hover:underline whitespace-nowrap flex-shrink-0 ml-4">
      {item.action || action} &rarr;
    </span>
  </>
);

// A row is a link to another page, or - for a video - a button that plays it
// in the overlay right here.
const PanelRows = ({ items, action, loading = false, onPlay = () => {}, showViewed = false }) => (
  <>
    <p className="flex font-semibold text-black border-b border-gray-300 pb-2">
      {showViewed && <span className="w-20 flex-shrink-0 text-center" title="Reports you have opened">Reviewed</span>}
      <span>Title</span>
    </p>
    <div className={PANEL_SCROLL}>
      {loading ? (
        <p className="text-gray-400 italic py-3">Loading&hellip;</p>
      ) : items.length === 0 ? (
        <p className="text-gray-500 italic py-3">Nothing here yet.</p>
      ) : items.map((item) => (
        item.video ? (
          <button
            key={item.video.videoId}
            type="button"
            onClick={() => onPlay(item.video)}
            className={ROW_CLASS}
            aria-label={`Play ${item.label}`}
          >
            <RowBody item={item} action={action} />
          </button>
        ) : (
          // The whole row is the link (10-07), not just the action word.
          <Link key={item.to} to={item.to} className={showViewed && item.isNew ? NEW_ROW_CLASS : ROW_CLASS}>
            <RowBody item={item} action={action} showViewed={showViewed} />
          </Link>
        )
      ))}
    </div>
  </>
);

const TabButtons = ({ tabs, active, onSelect }) => (
  <div className="ml-auto flex flex-wrap justify-end gap-2">
    {tabs.map((t) => (
      <button
        key={t.id}
        type="button"
        onClick={() => onSelect(t.id)}
        className={`px-3 py-1.5 rounded-lg text-sm border transition-colors ${
          active === t.id
            ? 'bg-black text-white border-black'
            : 'bg-white text-black border-gray-400 hover:bg-gray-50'
        }`}
      >
        {t.label}{t.count != null ? ` (${t.count})` : ''}
      </button>
    ))}
  </div>
);

const PanelShell = ({ heading, right, children }) => (
  <div className="px-6 pb-6">
    <div className="flex items-center gap-4 mb-4">
      <AiMark />
      <h3 className="text-2xl font-bold text-black">{heading}</h3>
      {right}
    </div>
    {children}
  </div>
);

const PanelList = ({ heading, items, action }) => (
  <PanelShell heading={heading}>
    <PanelRows items={items} action={action} />
  </PanelShell>
);

// Av Library panel: one tab per agency plus Podcasts. Videos play in place.
const AvLibraryPanel = ({ onPlay }) => {
  const [tab, setTab] = useState(AV_SUB_TABS[0].id);
  const active = AV_SUB_TABS.find((t) => t.id === tab);
  return (
    <PanelShell
      heading={active.heading}
      right={<TabButtons tabs={AV_SUB_TABS.map((t) => ({ ...t, count: t.items.length }))} active={tab} onSelect={setTab} />}
    >
      <PanelRows items={active.items} action={active.action} onPlay={onPlay} />
    </PanelShell>
  );
};

// CyberFraud Alert panel: the five categories, loaded on first open.
const SCAMWIRE_TABS = [
  ...ALERT_SOURCES.map((s) => ({ id: s.id, label: AGENCY_TAB_LABEL[s.id] || s.label, heading: `${s.label} Alert`, action: 'Read' })),
  { id: 'hot-scams', label: 'ScamWire', heading: 'ScamWire', action: 'Learn more' },
];

const ScamWirePanel = ({ current }) => {
  const [tab, setTab] = useState(current?.kind === 'hot-scam' ? 'hot-scams' : (current?.source || SCAMWIRE_TABS[0].id));
  const [lists, setLists] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const get = (path) => fetch(`${API_BASE_URL}${path}`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .catch(() => ({ items: [] }));
    Promise.all([
      ...ALERT_SOURCES.map((s) => get(`/bff/ic3/alerts?limit=50&source=${s.id}`).then((d) => [s.id, (d.items || [])
        .filter((a) => !(current?.kind === 'alert' && String(a.id) === String(current.id)))
        .map((a) => ({ label: a.title, to: `/dashboard/fraud-alerts/${a.id}`, viewed: Boolean(a.viewed), isNew: isNew(a.published_at) }))])),
      get('/bff/hot-scams').then((d) => ['hot-scams', (d.items || [])
        .filter((s) => !(current?.kind === 'hot-scam' && s.slug === current.slug))
        .map((s) => ({ label: s.title, to: `/dashboard/hot-scams/${s.slug}`, viewed: Boolean(s.viewed), isNew: isNew(s.published_at) }))]),
    ]).then((pairs) => { if (!cancelled) setLists(Object.fromEntries(pairs)); });
    return () => { cancelled = true; };
  }, [current?.kind, current?.id, current?.slug]);

  const active = SCAMWIRE_TABS.find((t) => t.id === tab);
  const items = lists ? lists[tab] || [] : [];
  return (
    <PanelShell
      heading={active.heading}
      right={<TabButtons tabs={SCAMWIRE_TABS.map((t) => ({ ...t, count: lists ? (lists[t.id] || []).length : null }))} active={tab} onSelect={setTab} />}
    >
      <PanelRows items={items} action={active.action} loading={!lists} showViewed />
    </PanelShell>
  );
};

const HubArticleFrame = ({ title, current, openPanel = null, onTogglePanel = () => {}, children }) => {
  const [playing, setPlaying] = useState(null);
  return (
  <div className="relative border-2 border-black mt-10">
    {/* Title sits across the top border like a legend. It is in flow (not
        absolute) so a long title wraps inside the frame instead of running
        past it into the Report rail; the sub-tab row follows it naturally. */}
    <div className="-mt-6 mx-4">
      <h1 className="inline-block max-w-full bg-white px-3 text-3xl xl:text-4xl font-bold leading-tight text-black">
        {title}
      </h1>
    </div>

    {/* Sub-tab row */}
    <div className="flex flex-wrap justify-end gap-x-6 xl:gap-x-10 gap-y-2 px-6 pt-4 pb-4">
      {SUB_TABS.map((tab) => {
        const isOpen = openPanel === tab.id;
        return (
          <button
            key={tab.id}
            type="button"
            onClick={() => onTogglePanel(isOpen ? null : tab.id)}
            className={`text-base whitespace-nowrap transition-colors ${
              isOpen ? 'font-bold text-black' : 'text-gray-800 hover:text-black'
            }`}
          >
            {tab.label} {isOpen ? '^' : 'v'}
          </button>
        );
      })}
    </div>

    {/* Panel content or the page's own hero */}
    {openPanel === 'scamwire' ? (
      <ScamWirePanel current={current} />
    ) : openPanel === 'av-library' ? (
      <AvLibraryPanel onPlay={setPlaying} />
    ) : openPanel === 'dark-psychology' ? (
      <PanelList heading={SUB_TABS[2].heading} items={DARK_PSYCHOLOGY_ITEMS} action={SUB_TABS[2].action} />
    ) : openPanel === 'detection' ? (
      <PanelList heading={SUB_TABS[3].heading} items={DETECTION_ITEMS} action={SUB_TABS[3].action} />
    ) : (
      <div className="px-6 pb-6">{children}</div>
    )}

    <VideoPlayerModal video={playing} onClose={() => setPlaying(null)} />
  </div>
  );
};

export default HubArticleFrame;
